import { Injectable } from "@angular/core";
import { ProjectDbService } from "@app/project/services/project-db.service";
import { ProjectDispatchers } from "@app/project/store/dispatchers/project.dispatchers";
import { SnackbarActions } from "@app/root-store/actions";
import { ActionWord } from "@app/shared/resources/action-word";
import { HttpResponseHandler } from "@app/shared/services/http-response-handler";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action } from "@ngrx/store";
import { Observable, of } from "rxjs";
import { catchError, exhaustMap, map, switchMap } from "rxjs/operators";
import { AlulaActions, DataActions, SubscriptionActions } from "../actions";
import { ClareSubscriptionDbService } from "../services/subscription-db.service";

const toAction = DataActions.toAction();
const subscriptionActionTypes = SubscriptionActions.ActionTypes;

export type ChangeSubscriptionAction =
    | SubscriptionActions.ConvertSubscriptionSuccess
    | SubscriptionActions.UpdateSubscriptionSuccess
    | SubscriptionActions.CreateSubscriptionSuccess;

@Injectable()
export class SubscriptionEffects {
    constructor(
        private actions$: Actions,
        private subscriptionDbService: ClareSubscriptionDbService,
        private projectDbService: ProjectDbService,
        private projectDispatcher: ProjectDispatchers
    ) {}

    getSubscriptionsByProject$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(subscriptionActionTypes.GetActiveSubscriptionByProjectId),
            switchMap((action: SubscriptionActions.GetActiveSubscriptionByProjectId) =>
                toAction(
                    this.subscriptionDbService.getActiveSubscriptionByProject(action.payload),
                    SubscriptionActions.GetActiveSubscriptionByProjectIdSuccess,
                    SubscriptionActions.GetActiveSubscriptionByProjectIdFailure
                )
            )
        )
    );

    updateSubscription$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(subscriptionActionTypes.UpdateSubscription),
            exhaustMap((action: SubscriptionActions.UpdateSubscription) =>
                toAction(
                    this.subscriptionDbService.update(action.payload).pipe(
                        map(() => {
                            const features = action.additionalFeatures;
                            if (features.length) {
                                this.projectDbService.updateFeaturesStatus(features, action.uuid, action.project);
                            }
                            return {
                                subscription: action.payload,
                                uuid: action.uuid,
                                newServicePlan: action.newServicePlan,
                                additionalFeatures: action.additionalFeatures,
                                isProjectOffline: action.isProjectOffline,
                            };
                        }),
                        // @TODO: Why are we suppressing errors and passing it to the success action?
                        catchError(e => of(e))
                    ),
                    SubscriptionActions.UpdateSubscriptionSuccess,
                    SubscriptionActions.UpdateSubscriptionFailure,
                    action.emitter
                )
            ),
            catchError(DataActions.toError(SubscriptionActions.UpdateSubscriptionFailure))
        )
    );

    createSubscription$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(subscriptionActionTypes.CreateSubscription),
            switchMap((action: SubscriptionActions.CreateSubscription) =>
                toAction(
                    this.subscriptionDbService.create(action.payload).pipe(
                        map(result => {
                            const features = action.additionalFeatures;
                            if (features.length) {
                                this.projectDbService.updateFeaturesStatus(features, action.uuid, action.project);
                            }
                            return {
                                subscription: result,
                                uuid: action.uuid,
                                newServicePlan: action.newServicePlan,
                                additionalFeatures: action.additionalFeatures,
                                isProjectOffline: action.isProjectOffline,
                            };
                        }),
                        catchError(e => of(e))
                    ),
                    SubscriptionActions.CreateSubscriptionSuccess,
                    SubscriptionActions.CreateSubscriptionFailure,
                    action.emitter
                )
            ),
            catchError(DataActions.toError(SubscriptionActions.CreateSubscriptionFailure))
        )
    );

    convertSubscription$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(subscriptionActionTypes.ConvertSubscription),
            switchMap((action: SubscriptionActions.ConvertSubscription) =>
                toAction(
                    this.subscriptionDbService
                        .convert(
                            action.payload.currentSubscription,
                            action.payload.currentServicePlan,
                            action.payload.newSubscription,
                            action.payload.newServicePlan,
                            action.payload.alulaDevice,
                            action.payload.cellularDevice
                        )
                        .pipe(
                            map(result => {
                                const features = action.payload.additionalFeatures;
                                if (features.length) {
                                    this.projectDbService.updateFeaturesStatus(features, action.payload.uuid, action.payload.project);
                                }
                                return {
                                    previous: result.previous,
                                    current: result.current,
                                    uuid: action.payload.uuid,
                                    newServicePlan: action.payload.newServicePlan,
                                    additionalFeatures: action.payload.additionalFeatures,
                                    isProjectOffline: action.payload.isProjectOffline,
                                };
                            }),
                            catchError(e => of(e))
                        ),
                    SubscriptionActions.ConvertSubscriptionSuccess,
                    SubscriptionActions.UpdateSubscriptionFailure,
                    action.onSuccess
                )
            )
        )
    );

    convertSubscriptionSuccess$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(subscriptionActionTypes.ConvertSubscriptionSuccess),
            map(_ => new AlulaActions.ClearAlulaState())
        )
    );

    changeSubscriptionSuccess$: Observable<any> = createEffect(() =>
        this.actions$.pipe(
            ofType(
                subscriptionActionTypes.ConvertSubscriptionSuccess,
                subscriptionActionTypes.UpdateSubscriptionSuccess,
                subscriptionActionTypes.CreateSubscriptionSuccess
            ),
            switchMap(
                (
                    action:
                        | SubscriptionActions.ConvertSubscriptionSuccess
                        | SubscriptionActions.UpdateSubscriptionSuccess
                        | SubscriptionActions.CreateSubscriptionSuccess
                ) =>
                    this.subscriptionDbService.updateEquator(action.result).pipe(
                        map(_ => {
                            const message = new HttpResponseHandler("Service Plan", ActionWord.saved);
                            return SnackbarActions.openSnackbar({ message });
                        }),
                        catchError(_ => {
                            const isProjectOffline = action.result?.isProjectOffline;
                            if (isProjectOffline) {
                                const errorMessage =
                                    "ClareOne panel offline, changes to service plans are updated immediately in FusionPro. The ClareOne panel will receive the change when back online.";
                                const message = new HttpResponseHandler(null, null, errorMessage, true);
                                return of(SnackbarActions.openSnackbar({ message }));
                            }
                            //in case, no update features for online project
                            const message = new HttpResponseHandler("Service Plan", ActionWord.saved);
                            return of(SnackbarActions.openSnackbar({ message }));
                        })
                    )
            )
        )
    );
}
